/**
 * "Mein Zimmer" — the merged room page (COL-533).
 *
 * V1 spread this across four tabs (Vertrag, Zahlungen, Ein-/Auszug,
 * Dokumente). The design puts them on one page as four stacked sections.
 *
 * Section order is FIXED — Vertrag, Zahlungen, Ein- & Auszug, Infos — not
 * driven by journey phase. The export computes a `firstSec` variable that
 * looks like phase-ordering, but it is declared once and referenced nowhere:
 * the markup binds `ord.vertrag`/`ord.zahlungen`/`ord.einzug`/`ord.infos`,
 * whose CSS `order` is a constant 1/2/3/4 at every phase. Implementing
 * phase-ordering here would be inventing behaviour the design does not have.
 *
 * Money figures come from the canonical resolvers in `lib/rent.ts` rather than
 * being recomputed: expected deposit is the stored `bookings.deposit_amount`
 * (never a sum of billed rows, which drifts on a rent raise), and monthly rent
 * is `bookings.monthly_rent` with `rooms.price` only as a legacy fallback.
 */

import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { ProtocolFlow } from "@/components/protocol/ProtocolFlow";
import ContractSignBlock from "@/components/tenant/v2/ContractSignBlock";
import TenantHandoverRows from "@/components/tenant/v2/TenantHandoverRows";
import TenantPaymentsSection from "@/components/tenant/v2/TenantPaymentsSection";
import { Button } from "@/components/ui/button";
import { useTenantMoveInDocuments } from "@/hooks/useTenantPaymentMethod";
import {
  openHouseDocument,
  openTenantDocument,
} from "@/lib/tenant-document-url";
import {
  houseDocumentsReleased,
  HOUSE_INFO_DOCUMENTS,
} from "@/lib/tenant-house-documents";
import { Card } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { translateDictionary } from "@/lib/i18n/dictionary";
import { baseLanguageFor, localeFor } from "@/lib/i18n/languages";
import {
  getExpectedDepositEUR,
  getFullMonthlyRentFromBooking,
} from "@/lib/rent";
import { getTenantRoomLabels } from "@/lib/tenant-room-copy";
import { isContractAwaitingTenantSignature } from "@/lib/tenant-payment-scope";
import { formatTenantDate } from "@/lib/date-format";
import type { SearchTarget } from "@/lib/tenant-dashboard-search";
import {
  hasContractDocument,
  isContractSentForSignature,
} from "@/lib/tenantJourney";

type Props = {
  booking: any;
  /** Hide the handover block while a protocol step is open, as V1 does. */
  protocolStepOpen?: boolean;
};

function Section({
  title,
  anchor,
  children,
}: {
  title: string;
  /** Where a dashboard search result for this section lands (COL-623). */
  anchor: SearchTarget;
  children: React.ReactNode;
}) {
  return (
    <section
      data-search-target={anchor}
      className="grid gap-4 border-t border-border/60 pt-8 md:grid-cols-[minmax(0,260px)_minmax(0,1fr)] md:gap-10"
    >
      <h2 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
        {title}
      </h2>
      <div className="min-w-0 space-y-4">{children}</div>
    </section>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <p className="text-sm text-muted-foreground">{label}</p>
      {/*
        Wraps rather than truncates: these values are addresses and date
        ranges, and half an address on a phone is worse than two lines.
        `break-words` keeps a long unbroken string from widening the grid
        column and pushing the page into horizontal scroll.
      */}
      <p className="break-words font-semibold text-foreground">{value}</p>
    </div>
  );
}

/**
 * One label/value line in the design's Info & documents card.
 *
 * The design stacks a 13.5px muted label over a 15.5px semibold value, with
 * a hairline above every row but the first — hence `border-t` plus a
 * `first:border-t-0`, rather than a divider element per row.
 */
function InfoRow({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  /** Optional muted line under the value (e.g. the floor under the room name). */
  detail?: string;
}) {
  return (
    <div className="min-w-0 border-t border-input py-3.5 first:border-t-0">
      <p className="text-[13.5px] text-muted-foreground">{label}</p>
      <p className="break-words font-semibold text-foreground">{value}</p>
      {detail ? (
        <p className="break-words text-[13.5px] text-muted-foreground">
          {detail}
        </p>
      ) : null}
    </div>
  );
}

/**
 * One document line: title, sub-line, and either a PDF button or the
 * design's muted "from move-in" pill when the document is not out yet.
 */
function DocRow({
  title,
  meta,
  pdfLabel,
  lockedLabel,
  onOpen,
  anchor,
}: {
  title: string;
  meta: string;
  pdfLabel: string;
  /** When set, the row shows this pill instead of a button. */
  lockedLabel?: string;
  onOpen?: () => void;
  /** Where a dashboard search result for this document lands (COL-623). */
  anchor?: SearchTarget;
}) {
  return (
    <div
      data-search-target={anchor}
      className="flex items-center justify-between gap-4 border-t border-input py-3.5 first:border-t-0"
    >
      <div className="min-w-0">
        <p className="font-semibold text-foreground">{title}</p>
        <p className="mt-0.5 text-[13.5px] text-muted-foreground">{meta}</p>
      </div>
      {lockedLabel ? (
        <span className="shrink-0 whitespace-nowrap rounded-full bg-[rgba(138,107,84,0.12)] px-3 py-1.5 text-[13px] font-semibold text-[#8A6B54]">
          {lockedLabel}
        </span>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          className="shrink-0 rounded-full bg-card hover:bg-card/80"
          onClick={onOpen}
        >
          {pdfLabel}
        </Button>
      )}
    </div>
  );
}

export default function TenantRoomPage({ booking, protocolStepOpen }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();

  /*
    Open a protocol step. Same parameters `ProtocolFlow`'s own entry writes
    (`p` set, `step` dropped so the resume router picks the right one) — but
    the tab is left alone. V1 sets `tab=checkin`; doing that here would
    navigate the tenant off My room mid-flow.
  */
  const startProtocol = (protocolId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("p", protocolId);
    next.delete("step");
    setSearchParams(next, { replace: true });
  };

  const { tDict, language: activeLanguage } = useLanguage();
  const language = baseLanguageFor(activeLanguage);
  const d = translateDictionary(
    "tenant.room",
    getTenantRoomLabels(language),
    (key, literal) => tDict(key, literal ?? "", activeLanguage)
  );

  // Dates and currency follow the ACTIVE language, not the de/en copy base, so
  // a French tenant gets French formatting rather than British — same rule
  // Dashboard.tsx applies.
  const locale = localeFor(activeLanguage);
  // Cents kept, not rounded away. A 976.50 deposit displayed as "977" would
  // contradict the payments ledger lower down this same page, which formats
  // with the default two digits, and the contract PDF.
  const eur = (n: number) =>
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "EUR",
    }).format(n);

  const day = (iso: string | null | undefined) =>
    iso ? formatTenantDate(iso) : "—";

  const rent = getFullMonthlyRentFromBooking(booking);
  const deposit = getExpectedDepositEUR(booking);

  // Precedence matches V1, which reads `move_in_date || start_date` and
  // `move_out_date || end_date` at every one of its ~14 call sites — the
  // rent schedule, the booking cards and the Wohnungsgeberbestätigung among
  // them. Reversed, a booking whose actual move-in differs from its
  // contractual start showed one date here and another everywhere else,
  // including on the protocol card further down this same page.
  const start = booking?.move_in_date ?? booking?.start_date ?? null;
  const end = booking?.move_out_date ?? booking?.end_date ?? null;
  const term = start || end ? `${day(start)} – ${day(end)}` : "—";

  // A released contract waiting on the tenant — the same predicate the
  // journey resolver and the admin contract area use.
  // Outstanding signature only. isContractSentForSignature stays true after
  // signing, so it would keep offering "sign now" to a signed tenant.
  const awaitingSignature = isContractAwaitingTenantSignature(booking);
  const contractPdf = hasContractDocument(booking);

  /*
    V1 cannot show the contract for a move-out tenancy.

    Its own booking query lists only confirmed/pending/active/approved
    (Dashboard.tsx:1105), so for `notice_given` and `move_out_pending` — the
    two states THIS page newly supports — the contract tab falls through to
    ComingSoon. Linking there would send a tenant from a page that has their
    contract to one that tells them it is not built yet. Link the document
    itself when we hold a URL for it, and otherwise show nothing.
  */
  /*
    GATED, because these columns are tenant-writable.

    `bookings.signed_contract_url` can be written by the tenant, so a stored
    `javascript:` or `data:` value opened from here would run as us and could
    read the session. COL-436 removed every ungated href of exactly this
    column on develop — its last commit calls the one it fixed "the last
    ungated href" — and this page would have reintroduced one. `isOpenableUrl`
    is that fix's own predicate: http(s) and blob only.
  */
  const rawContractHref: string | null =
    booking?.signed_contract_url ||
    booking?.contract_pdf_url ||
    booking?.contract_url ||
    null;
  /*
    SIGNED at click time, never rendered as a raw href.

    The stored value is whatever `getPublicUrl` returned at upload
    (`…/object/public/contracts/<path>`), and that URL dies the moment the
    bucket is private or absent — on dev it answers
    `{"statusCode":"404","error":"Bucket not found"}`, which is what a tenant
    saw after clicking a button labelled PDF. `resolveStoredFileUrl` (COL-436)
    takes the bucket and path back out of the stored value and mints a
    short-lived signed URL, which works on a public bucket too. Every other
    document row on this page already reads through it; this one did not.

    It also subsumes the `isOpenableUrl` gate that used to sit here. These
    columns are tenant-writable, and the resolver returns null for anything
    that is not one of our own Storage objects — a `javascript:` or `data:`
    value resolves to nothing and surfaces as "could not be opened" rather
    than becoming an href that runs as us.
  */
  /*
    Gated on release, not just presence.

    `buildGenerateUpdate` (create-contract) writes `contract_pdf_url` in the
    SAME update that sets `contract_status = 'draft'` -- a draft that has
    never been released to the tenant still has a stored PDF. Without this
    gate the button above would open that file: a pre-release contract
    leaking to the tenant it isn't ready for. `isContractSentForSignature`
    is the same release gate the journey ladder already uses, so a bare
    draft (no docusign/signed signal) resolves to no href here, and the
    row falls through to "Not available yet" instead.
  */
  const contractHref =
    rawContractHref && isContractSentForSignature(booking)
      ? String(rawContractHref)
      : null;
  const openContract = async () => {
    if (!contractHref) return;
    const ok = await openTenantDocument(contractHref);
    // Say so, rather than leaving a blank tab or a JSON error page.
    if (!ok) toast.error(d.documentUnavailable);
  };

  // `contract_signed_at` is a TIMESTAMP, not a date-only column, so plain
  // `new Date` is correct here — unlike the booking's move-in/out dates.
  const signedOn = booking?.contract_signed_at
    ? day(String(booking.contract_signed_at))
    : null;
  const contractVersion = Number(booking?.contract_version ?? 1) || 1;

  const { data: moveInDocs = [] } = useTenantMoveInDocuments(booking?.id);
  /*
    `move_in_date` ONLY — no `start_date` fallback.

    The email this gate is meant to track anchors on `move_in_date` and skips
    a booking without one, twice over: the candidate query filters
    `.not("move_in_date", "is", null)` and `processBooking` returns early on a
    null anchor. Falling back to `start_date` would open the documents for a
    booking that is never going to receive the mail — the opposite of "the
    same day the email gets sent".
  */
  const houseDocsOpen = houseDocumentsReleased(booking?.move_in_date);

  const room = booking?.rooms;
  const property = room?.properties;

  return (
    <div className="space-y-10">
      <Section title={d.contract} anchor="room-contract">
        <Card className="p-6">
          <div className="grid gap-6 sm:grid-cols-3">
            <Fact label={d.term} value={term} />
            <Fact label={d.rentAllIn} value={rent > 0 ? eur(rent) : "—"} />
            <Fact label={d.deposit} value={deposit > 0 ? eur(deposit) : "—"} />
          </div>

          {/*
            Signing is not ported yet, and `?tab=contract` links emailed by
            send-contract-to-docusign land on this page. Without an explicit
            route back to V1 a tenant told to sign arrives somewhere with no
            way to do it — the failure this block exists to prevent.
          */}
          {/*
            The design's contract ROW: a title, the signed date and version,
            and a PDF pill on the right. `contract_signed_at` and
            `contract_version` are both real columns, so nothing here is
            invented — the version falls back to standing alone when a
            contract is on file but not yet signed.
          */}
          {/*
            Shown while awaiting signature too, which it was not before.

            The sign block below asks the tenant to confirm "I have read the
            contract and house rules" — so the contract has to be readable on
            this page BEFORE that box, or they are declaring they read
            something we never showed them. The export draws it this way as
            well: Rental contract + PDF sits directly above the checkbox.
          */}
          {contractPdf ? (
            <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-border/60 pt-6">
              <div className="min-w-0">
                <p className="font-semibold text-foreground">
                  {d.contractRowTitle}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {signedOn
                    ? d.contractSignedOn
                        .replace("{date}", signedOn)
                        .replace("{version}", String(contractVersion))
                    : d.contractVersionOnly.replace(
                        "{version}",
                        String(contractVersion)
                      )}
                </p>
              </div>
              {/*
                The DOCUMENT first; V1 only as the fallback.

                These two branches used to be the other way round, and
                `v1CanShowContract` is true for confirmed/pending/active/
                approved -- essentially every real tenant. So the
                `contractHref` branch was unreachable for them: a button
                labelled "PDF" navigated to the old My Contract screen and
                showed a summary, never the file. Neel reported exactly that
                (13 Oct): a control labelled PDF opens the old My Contract
                screen instead of displaying a PDF.

                So the control now does one of exactly two things: open the
                document, or say it is not available yet. It never navigates
                somewhere else under a "PDF" label.
              */}
              {contractHref ? (
                <Button
                  variant="secondary"
                  size="sm"
                  className="rounded-full"
                  onClick={() => void openContract()}
                >
                  {d.contractPdfPill}
                </Button>
              ) : (
                /*
                  No openable document: say so, rather than offer a button
                  labelled "PDF" that goes somewhere else.

                  This REPLACES a hand-off to V1's contract tab. Neel's
                  acceptance criterion is explicit -- "if it is unavailable,
                  show a clear unavailable state rather than presenting a
                  misleading PDF link" -- and that hand-off was the misleading
                  link: it produced a contract SUMMARY, never a file, which is
                  what made the control feel broken.

                  Nothing is stranded by dropping it. The row only renders
                  when `hasContractDocument` is true, so the state reaching
                  here is "an envelope exists, no file yet" -- a contract out
                  for signature. Signing lives on THIS page, in the block
                  directly below; V1's tab added a summary of data already
                  shown in this very card.

                  Same muted pill the other document rows use for "from
                  move-in", so an unavailable document reads the same way
                  wherever it appears on this page.
                */
                <span className="shrink-0 whitespace-nowrap rounded-full bg-[rgba(138,107,84,0.12)] px-3 py-1.5 text-[13px] font-semibold text-[#8A6B54]">
                  {d.contractPdfPending}
                </span>
              )}
            </div>
          ) : null}

          {/*
            The design signs here, rather than sending the tenant back to
            V1 ("Signing still happens in the previous view"). The gates are
            V1's, in V1's order — checkbox, date of birth (COL-258), then an
            explicit confirm — and the calls are the shared ones, so this is
            a second presentation of the flow, not a second flow.
          */}
          {awaitingSignature ? (
            <ContractSignBlock
              bookingId={Number(booking.id)}
              hasEnvelope={
                String(booking?.docusign_envelope_id ?? "").trim() !== ""
              }
              labels={{
                readConfirm: d.readConfirm,
                signDigitally: d.signDigitally,
                openingDocusign: d.openingDocusign,
                openingThisTab: d.openingThisTab,
                confirmSignTitle: d.confirmSignTitle,
                confirmSignBody: d.confirmSignBody,
                confirmSignCta: d.confirmSignCta,
                cancel: d.cancel,
                dobTitle: d.dobTitle,
                dobBody: d.dobBody,
                dobLabel: d.dobLabel,
                dobSave: d.dobSave,
                dobEmpty: d.dobEmpty,
                dobInvalid: d.dobInvalid,
                dobUnderage: d.dobUnderage,
                signFailed: d.signFailed,
              }}
            />
          ) : null}
        </Card>
      </Section>

      <Section title={d.payments} anchor="room-payments">
        <TenantPaymentsSection booking={booking} />
      </Section>

      <Section title={d.moveInOut} anchor="room-move">
        {/*
          The design's two compact rows replace V1's hero card. When a
          protocol step IS open we hand straight back to `ProtocolFlow`,
          which owns the stepper — the rows are the entry point only, so
          the flow itself is untouched.
        */}
        {protocolStepOpen ? (
          <ProtocolFlow />
        ) : (
          /*
            V1's standalone check-in/out screen is NO LONGER MOUNTED here.

            It repeated the move-in/move-out state these rows already show,
            in the old layout, and the one thing it carried that nothing else
            did -- key pickup and key return -- now sits inside the rows it
            belongs to. Neel, 13 Oct: "integrate the useful information into
            Infos & Documents and simplify key pickup / return details...
            avoid a duplicate standalone check-in/check-out experience".

            `CheckInCheckOut` itself is untouched and still serves V1.
          */
          <TenantHandoverRows onStart={startProtocol} booking={booking} />
        )}
      </Section>

      <Section title={d.infoDocs} anchor="room-info">
        {/*
          The design's Info & documents card: beige, two columns — house
          info on the left, documents on the right. It collapses to one
          column on a phone, where two 13.5px columns would be unreadable.
        */}
        <Card className="rounded-[20px] border-0 bg-muted px-7 py-1.5">
          <div className="grid items-start gap-x-10 py-1.5 sm:grid-cols-2">
            <div className="flex min-w-0 flex-col">
              <InfoRow
                label={d.address}
                value={
                  [property?.address, property?.zip_code, property?.city]
                    .filter(Boolean)
                    .join(", ") || "\u2014"
                }
              />
              <InfoRow
                label={d.room}
                value={room?.title ?? "\u2014"}
                detail={
                  String(room?.floor ?? "").trim()
                    ? d.floorLine.replace("{floor}", String(room.floor).trim())
                    : undefined
                }
              />
              {/*
                The design's left column also carries Wi-Fi, parcels,
                laundry, bikes and the bin days. None of those exist as a
                column anywhere — Neel, in the COL-533 thread: "For the
                stuff you flagged, ignore those for now. we'll add later."
                They need a schema decision, not a UI guess, so they are
                left out rather than rendered permanently blank.
              */}
            </div>

            <div className="flex min-w-0 flex-col">
              {/*
                The Wohnungsgeberbestätigung, from the row the move-in mail
                already files. Ganesh: "we already generate it… saved to the
                tenant's documents, so V2 can show it from there." Neel:
                "we should continue to send by email AND embed it into the
                dashboard" — so this reads the filed row and changes nothing
                about the mail.

                Before it exists the row still shows, carrying the design's
                "from move-in" pill. Hiding the row entirely (what this did
                before) left a tenant wondering whether we had forgotten it.

                `openTenantDocument` resolves a SIGNED url (COL-436); the
                stored `file_url` is a public one that must be re-signed.
              */}
              <DocRow
                anchor="room-landlord-confirmation"
                title={d.landlordConfirmation}
                meta={
                  moveInDocs.length > 0
                    ? d.landlordConfirmationHint
                    : d.landlordConfirmationPending
                }
                pdfLabel={d.contractPdfPill}
                {...(moveInDocs.length > 0
                  ? {
                      onOpen: () => {
                        const url = moveInDocs[0]?.fileUrl;
                        if (url) void openTenantDocument(url);
                      },
                    }
                  : { lockedLabel: d.fromMoveIn })}
              />

              {/*
                The two static move-in explainers. Neel, in the COL-533
                thread: "For the other two, yes embed those two documents
                for now."

                These are the SAME objects the T-1 move-in email attaches,
                read straight out of the `templates` bucket rather than
                copied — so there is one file per document, and the mail
                keeps working exactly as it did.

                They stay locked until the day that email goes out. Neel:
                "both should only become available via the dashboard the
                same day/time the email gets sent." Before then the row
                still shows, carrying the design's "from move-in" pill, so
                the tenant can see what is coming.

                Once open, a missing object fails on click rather than
                leaving a blank tab — `openHouseDocument` reports it.
              */}
              {HOUSE_INFO_DOCUMENTS.map((doc) => (
                <DocRow
                  key={doc.key}
                  anchor={
                    doc.key === "wasteSorting"
                      ? "room-waste-sorting"
                      : "room-broadcasting-fee"
                  }
                  title={
                    doc.key === "wasteSorting"
                      ? d.wasteSortingDoc
                      : d.broadcastingFeeDoc
                  }
                  meta={
                    doc.key === "wasteSorting"
                      ? d.wasteSortingDocHint
                      : d.broadcastingFeeDocHint
                  }
                  pdfLabel={d.contractPdfPill}
                  {...(houseDocsOpen
                    ? {
                        onOpen: () => {
                          void openHouseDocument(doc.path).then((ok) => {
                            if (!ok) toast.error(d.documentUnavailable);
                          });
                        },
                      }
                    : { lockedLabel: d.fromMoveIn })}
                />
              ))}
            </div>
          </div>
        </Card>
      </Section>
    </div>
  );
}
